import type { Task } from "~skillBuilder/utils/types";

import { doDont, image, title } from "../../contentBlocks";

export const TspProfessionalism = {
  name: "Professionalism",
  uniqueName: "tsp-professionalism",
  icon: "Lesson",
  durationTimeText: "10 min",
  steps: [
    // Step 1
    {
      nextButtonText: "Start",
      questions: [
        // Question 1.1
        {
          attribute: "the-sub-playbook.professionalism/overview",
          label: "",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${title({ text: "Professionalism", durationTimeText: "10 min" })}
<p>Maintaining professionalism in a school setting sets the stage for a smooth, productive day for both you and your students. As a substitute teacher, a professional approach helps you make a great first impression, build trust with staff, and foster a positive learning environment. This training provides the foundational standards and practical tips to help you establish yourself as a trusted, star substitute.</p>
<div align="center">
<div style="max-width:300px;width:100%;">
${image({ url: "https://res.cloudinary.com/swingeducation/image/upload/fl_preserve_transparency/v1740759766/skillbuilderAssets/optimized/challenges.jpg", alt: "Illustration of a teacher standing confidently in front of a chalkboard with students." })}
</div>
</div>
`,
          },
        },
      ],
    },
    // Step 2
    {
      questions: [
        // Question 2.1
        {
          attribute: "the-sub-playbook.professionalism/foundations",
          label: "Foundations of Professionalism & School Expectations",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>Your attitude and appearance shape how students and staff perceive you. This lesson covers the core standards schools expect from every professional substitute.</p>
<p><strong>The Professional Image</strong></p>
<p>To project a professional image and demonstrate reliability, here are some baseline expectations schools have:</p>

- 🎯 **Professional Attire:** Wear clean, comfortable business casual clothing that follows the school&apos;s dress code.
- 🎯 **Preparedness:** Review lesson plans and have a backup activity ready, such as those from [Subplan.ai](https://subplan.ai).
- 🎯 **Personal Conduct:** Maintain good hygiene and avoid strong odors like perfumes or smoke. Keep personal electronic devices stowed away.
`,
          },
        },
      ],
    },
    // Step 3
    {
      questions: [
        // Question 3.1
        {
          attribute: "the-sub-playbook.professionalism/communication",
          label: "Professional Communication & Confidentiality",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
${doDont({
  doText:
    "Use active listening, speak clearly, and keep all interactions positive and solution-focused.",
  dontText: "Name-calling, yelling, or straying into age-inappropriate topics.",
})}
<p>⚠️ <strong>Confidentiality:</strong> Keep student information, behaviors, and school operations private at all times. Always follow school protocols if a situation requires reporting.</p>
`,
          },
        },
      ],
    },
    // Step 4
    {
      questions: [
        // Question 4.1
        {
          attribute: "the-sub-playbook.professionalism/first-impression",
          label: "Making a Great First Impression",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<ul style="margin-left: 16px;">
  <li>🧑‍🏫 <strong>In-Person:</strong> Greet staff and students with a positive attitude and set clear expectations immediately.</li>
  <li>💻 <strong>Online:</strong> Ensure your Swing teacher profile has a professional photo, a concise bio, and highlights qualifications and accomplishments.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 5
    {
      questions: [
        // Question 5.1
        {
          attribute: "the-sub-playbook.professionalism/conduct",
          label: "Professional Conduct & Classroom Management",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>Professionalism is about more than just showing up; it&rsquo;s about how you manage the learning environment and interact with others.</p>
<p><strong>Maintaining the Learning Environment</strong></p>
<ul style="margin-left: 16px;">
  <li>📋 <strong>Follow Instructions:</strong> Adhere strictly to bell schedules, emergency protocols, and the teacher&apos;s lesson plan.</li>
  <li>🫡 <strong>Respect Shared Spaces:</strong> Avoid sitting on or placing feet on furniture, and keep the classroom organized.</li>
  <li>🎬 <strong>Set Expectations:</strong> Establish firm but respectful boundaries with students from the start.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 6
    {
      questions: [
        // Question 6.1
        {
          attribute: "the-sub-playbook.professionalism/conflicts",
          label: "Handling Conflicts & De-escalation",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>When conflicts arise, use these &ldquo;Power Phrases&rdquo; and strategies to de-escalate:</p>
<ul style="margin-left: 16px;">
  <li>🧘🏽 <strong>Stay Calm:</strong> Keep your composure to set the tone for resolution.</li>
  <li>👂 <strong>Listen Actively:</strong> Use phrases like, &ldquo;I can see you&apos;re frustrated; let&apos;s find a way to get this done together&rdquo; to acknowledge feelings.</li>
  <li>✅ <strong>Offer Choices:</strong> Instead of demanding control, provide students with options to guide them toward a solution.</li>
  <li>👉🏾 <strong>Redirect:</strong> Quickly shift students back to the task at hand to provide everyone with a fresh start.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 7
    {
      questions: [
        // Question 7.1
        {
          attribute: "the-sub-playbook.professionalism/adaptability",
          label: "Adaptability & Problem Solving",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>Schedules shift and technology glitches happen. Staying adaptable ensures student learning remains the priority.</p>
<p><strong>Flexibility & Engagement</strong></p>
<ul style="margin-left: 16px;">
  <li>📗 <strong>Be Ready for Change:</strong> Approach schedule shifts or updated plans with a &ldquo;can-do&rdquo; mindset.</li>
  <li>🏆 <strong>Active Involvement:</strong> Participate in lessons alongside students to build connections and catch disruptive behavior early.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 8
    {
      questions: [
        // Question 8.1
        {
          attribute: "the-sub-playbook.professionalism/technology",
          label: "Navigating Technology & Seeking Support",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<ul style="margin-left: 16px;">
  <li>👩🏽‍💻 <strong>Tech Pivots:</strong> If classroom technology (like SMARTBoards or Chromebooks) fails, ask a student to share their screen while you walk the room to provide assistance. Check near the classroom phone for a &ldquo;Tech Support&rdquo; contact card.</li>
  <li>🆘 <strong>Know When to Ask:</strong> If student behavior becomes unmanageable or expectations are unclear, reach out to school staff or administrators for guidance.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 9
    {
      questions: [
        // Question 9.1
        {
          attribute: "the-sub-playbook.professionalism/reflection-wrap",
          label: "Reflection & Wrapping Up",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<ul style="margin-left: 16px;">
  <li>🧹 <strong>Tidy Up:</strong> Use the last 5 minutes to have students help clean the room. Leave the classroom in better condition than you found it.</li>
  <li>✍️ <strong>Teacher Note:</strong> Write a detailed summary including what was taught, any behavior issues, and follow-up items for the teacher.</li>
  <li>💬 <strong>Feedback:</strong> Use the Swing app to share your experience at the school.</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 10
    {
      questions: [
        // Question 10.1
        {
          attribute: "the-sub-playbook.professionalism/reflection-checklist",
          label: "Reflection & Growth Checklist",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p>At the end of your day, ask yourself:</p>
<ul style="margin-left: 16px;">
  <li>🏆 <strong>Success:</strong> What was my biggest win today?</li>
  <li>🔄 <strong>Opportunities:</strong> What could I have done differently?</li>
  <li>🎯 <strong>Interactions:</strong> Did I communicate clearly and maintain a professional demeanor?</li>
  <li>💡 <strong>Learning:</strong> Were there any situations today that I can grow from?</li>
</ul>
`,
          },
        },
      ],
    },
    // Step 11
    {
      nextButtonText: "Next Lesson",
      questions: [
        // Question 11.1
        {
          attribute: "the-sub-playbook.professionalism/strategy-action",
          label: "Strategy in Action",
          answerType: {
            type: "CONTENT",
            value: "valid",
            markdown: `
<p><strong>Situation:</strong></p>

You arrive at Westside High wearing business casual attire (slacks and a polo) and carrying a folder with a few &ldquo;emergency&rdquo; writing prompts you pulled from [Subplan.ai](https://subplan.ai). You&rsquo;ve checked in, greeted the office staff warmly, and are now in Mr. Henderson&rsquo;s classroom.

<p>When you arrive in the classroom, you find the lesson plan and the morning is going great. At about 10:30 the lesson plan transitions to an activity that requires students to watch a documentary on the SMARTBoard and answer questions. However, the login for the computer isn&apos;t working.</p>
<p><strong>Potential solution:</strong></p>
<p>Instead of sitting at the desk and telling students &ldquo;the internet is down,&rdquo; you apply the &ldquo;Tech Pivot.&rdquo; You ask a student to pull the video up on their Chromebook and project it.</p>
<p><strong>Engagement:</strong> While the video plays, you don&apos;t sit at the teacher desk and check your phone. You walk the room quietly, building connections and ensuring students stay on task.</p>
`,
          },
        },
      ],
    },
  ],
} as const satisfies Task;
